import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { captureException } from '@sentry/angular';
import { PublicationStatus, TimelineShift } from '@wilson/interfaces';
import {
  TimelineCreateNewShiftV2,
  TimelineRemoveShiftFromUserV2,
} from '@wilson/shift-timeline/state';
import { ShiftsService } from '@wilson/shifts';
import { NzMessageService } from 'ng-zorro-antd/message';
import { firstValueFrom } from 'rxjs';
import { SimplifiedShiftDrawerFormService } from '../../simplified-shift-drawer-form.service';
import { SimplifiedDrawerShift } from '../../types';
import { SimplifiedShiftReloadAndTimelineSyncService } from './simplified-shift-reload-and-timeline-sync.service';
import {
  ReloadedShift,
  SimplifiedShiftSaveStateChange,
} from './simplified-shift-save.types';

/**
 * Persists assignee changes and related timeline assignment side effects.
 */
@Injectable()
export class SimplifiedShiftAssigneeSaveService {
  private readonly shiftsService = inject(ShiftsService);
  private readonly message = inject(NzMessageService);
  private readonly translate = inject(TranslateService);
  private readonly store = inject(Store);
  private readonly reloadAndSync = inject(
    SimplifiedShiftReloadAndTimelineSyncService,
  );

  async saveAssigneeChange(params: {
    currentShift: SimplifiedDrawerShift;
    newUserId: string | null;
    previousUserId: string | null;
    formService: SimplifiedShiftDrawerFormService;
    onSaveStateChange: SimplifiedShiftSaveStateChange;
    onShiftUpdated: (shift: ReloadedShift) => void;
    onUserIdSynced: (userId: string | null) => void;
  }): Promise<void> {
    const {
      currentShift,
      newUserId,
      previousUserId,
      formService,
      onSaveStateChange,
      onShiftUpdated,
      onUserIdSynced,
    } = params;

    onSaveStateChange({ isSaving: true, isSuccessfullySaved: false });

    try {
      const isCellView = this.reloadAndSync.isCellView();

      if (newUserId) {
        await firstValueFrom(
          this.shiftsService.updateShiftsBulk([
            {
              id: currentShift.id,
              userId: newUserId,
              organizationalUnitId: currentShift.organizationalUnitId,
              publicationStatus:
                currentShift.publicationStatus === PublicationStatus.Published
                  ? PublicationStatus.NotPublishedAgain
                  : currentShift.publicationStatus,
            },
          ]),
        );

        const timelineShift = this.reloadAndSync.getTimelineShiftForAssignment(
          currentShift,
          previousUserId,
        );

        if (previousUserId) {
          this.store.dispatch(
            new TimelineRemoveShiftFromUserV2({
              userId: previousUserId,
              shiftId: currentShift.id,
            }),
          );
        } else {
          this.reloadAndSync.migrateUnassignedActivitiesIfNeeded(
            currentShift.id,
            isCellView,
          );
          this.reloadAndSync.removeShiftFromUnassignedRegion(
            currentShift.id,
            isCellView,
          );
        }

        this.store.dispatch(
          new TimelineCreateNewShiftV2(
            {
              ...timelineShift,
              userId: newUserId,
              isHighlighted: true,
              publicationStatus: PublicationStatus.NotPublished,
            } as TimelineShift,
            newUserId,
          ),
        );

        const userIdsToUpdate = previousUserId
          ? [newUserId, previousUserId]
          : [newUserId];
        await firstValueFrom(
          this.reloadAndSync.updateUsersAssignmentsState(userIdsToUpdate),
        );
        this.reloadAndSync.updateProductivityAndKpisOfUsers(userIdsToUpdate);
      } else if (previousUserId) {
        await firstValueFrom(
          this.shiftsService.withdrawShifts([currentShift.id]),
        );

        this.reloadAndSync.addShiftToUnassignedRegion(currentShift, isCellView);
        this.store.dispatch(
          new TimelineRemoveShiftFromUserV2({
            userId: previousUserId,
            shiftId: currentShift.id,
          }),
        );

        await firstValueFrom(
          this.reloadAndSync.updateUsersAssignmentsState([previousUserId]),
        );
        this.reloadAndSync.updateProductivityAndKpisOfUsers([previousUserId]);
      } else {
        onSaveStateChange({ isSaving: false, isSuccessfullySaved: false });
        return;
      }

      const reloadedShift =
        await this.reloadAndSync.reloadShiftAndUpdateTimeline(currentShift.id);

      if (reloadedShift) {
        onUserIdSynced(reloadedShift.userId ?? null);
        formService.patchFromShift(reloadedShift, {
          preserveTierTwoPromotion: true,
        });
        onShiftUpdated(reloadedShift);
      }

      onSaveStateChange({ isSaving: false, isSuccessfullySaved: true });
    } catch (error) {
      captureException(error);
      this.message.error(
        this.translate.instant('general.something_went_wrong'),
      );
      onSaveStateChange({ isSaving: false, isSuccessfullySaved: false });
    }
  }
}
